// ============================================
// pages/unloading/UnloadingEntriesList.tsx — Unloading Entries with approval workflow
// Each entry = one batch of unloading at a godown (per-day). Bills aggregate from these.
// ============================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageOpen, Loader2, Plus, Eye, Edit2, CheckCircle, XCircle, ShieldCheck, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { seasonsAPI, districtsAPI, commoditiesAPI, unloadingEntriesAPI } from '../../api/services';
import { UserRole, formatAmount, num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  draft:            { label: 'Draft',            bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300' },
  submitted:        { label: 'Submitted',        bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-300' },
  dm_verified:      { label: 'DM Verified',      bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300' },
  manager_approved: { label: 'Manager Approved', bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-300' },
  rejected:         { label: 'Rejected',         bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-300' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-300' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
};

export const UnloadingEntriesList: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const canSubmit = hasRole(UserRole.DM, UserRole.WAREHOUSE_MANAGER, UserRole.SUPER_ADMIN);
  const canVerify = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canApprove = hasRole(UserRole.MD, UserRole.SUPER_ADMIN);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [entries, setEntries] = useState<any[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [districtFilter, setDistrictFilter] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [rejectModal, setRejectModal] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [s, d, c] = await Promise.all([seasonsAPI.list(), districtsAPI.list(), commoditiesAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setDistricts(d.data);
        setCommodities(c.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load data' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (commodityFilter) params.commodity_id = commodityFilter;
        if (districtFilter) params.district_id = districtFilter;
        const res = await unloadingEntriesAPI.list(selectedSeasonId, params);
        let list = Array.isArray(res.data) ? res.data : [];
        if (statusFilter !== 'all') list = list.filter((e: any) => (e.status || 'draft') === statusFilter);
        setEntries(list);
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load entries' });
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId, commodityFilter, districtFilter, statusFilter]);

  const advance = async (id: number, fn: () => Promise<any>, nextStatus: string, msg: string) => {
    setActionLoading(id);
    setMessage(null);
    try {
      await fn();
      setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, status: nextStatus } : e)));
      setMessage({ type: 'success', text: msg });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || `Failed: ${msg}` });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmit = (id: number) =>
    window.confirm('Submit this entry for verification?') &&
    advance(id, () => unloadingEntriesAPI.submit(selectedSeasonId, id), 'submitted', 'Submitted');

  const handleVerify = (id: number) =>
    window.confirm('Verify this entry?') &&
    advance(id, () => unloadingEntriesAPI.verify(selectedSeasonId, id, {}), 'dm_verified', 'Verified');

  const handleApprove = (id: number) =>
    window.confirm('Approve this entry?') &&
    advance(id, () => unloadingEntriesAPI.approve(selectedSeasonId, id, {}), 'manager_approved', 'Approved');

  const handleReject = async () => {
    if (!rejectModal || !rejectReason.trim()) return;
    setActionLoading(rejectModal);
    setMessage(null);
    try {
      await unloadingEntriesAPI.reject(selectedSeasonId, rejectModal, rejectReason.trim());
      setEntries((prev) => prev.map((e) => (e.id === rejectModal ? { ...e, status: 'rejected' } : e)));
      setMessage({ type: 'success', text: 'Rejected' });
      setRejectModal(null);
      setRejectReason('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to reject' });
    } finally {
      setActionLoading(null);
    }
  };

  const districtMap = Object.fromEntries(districts.map((d) => [d.id, d.name]));
  const commodityMap = Object.fromEntries(commodities.map((c: any) => [c.id, c.name]));

  const formatDate = (d?: string) => {
    if (!d) return '--';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
  };

  if (loading && entries.length === 0) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <PackageOpen className="w-7 h-7 text-blue-600" />
            Unloading Entries
          </h1>
          <p className="text-sm text-gray-500 mt-1">Per-batch records of unloading operations; bills aggregate from these.</p>
        </div>
        <button onClick={() => navigate('/unloading/entries/new')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm">
          <Plus className="w-4 h-4" />Add New Entry
        </button>
      </div>

      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {seasons.map((s) => <option key={s.id} value={s.id}>{s.season_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Commodity</label>
            <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All Commodities</option>
              {commodities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">District</label>
            <select value={districtFilter} onChange={(e) => setDistrictFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All Districts</option>
              {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="dm_verified">DM Verified</option>
              <option value="manager_approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Entry ID</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">District</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Godown</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contractor</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Bags</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Wt (Qtl)</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Claimed</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Verified</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {entries.length === 0 ? (
                <tr><td colSpan={12} className="px-4 py-12 text-center text-gray-400">
                  No entries found. Click "Add New Entry" to create one.
                </td></tr>
              ) : (
                entries.map((e, i) => {
                  const st = e.status || 'draft';
                  const isActing = actionLoading === e.id;
                  return (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-xs text-gray-500">{i + 1}</td>
                      <td className="px-3 py-3 text-xs font-medium text-gray-900">{e.entry_id_ext || `#${e.id}`}</td>
                      <td className="px-3 py-3 text-xs text-gray-600">{formatDate(e.entry_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{e.district?.name || districtMap[e.district_id] || '--'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{e.godown?.godown_name || '--'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{e.contractor?.firm_name || '--'}</td>
                      <td className="px-3 py-3 text-xs text-right text-gray-700">{num(e.bags_unloaded).toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-xs text-right text-gray-700">{num(e.weight_qtl).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-3 text-xs text-right text-gray-700">{formatAmount(num(e.amount_claimed))}</td>
                      <td className="px-3 py-3 text-xs text-right font-medium text-gray-900">{formatAmount(num(e.amount_verified))}</td>
                      <td className="px-3 py-3 text-center"><StatusBadge status={st} /></td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          <button onClick={() => navigate(`/unloading/entries/${e.id}`)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">
                            {(st === 'draft' || st === 'rejected') ? <><Edit2 className="w-3 h-3" /> Edit</> : <><Eye className="w-3 h-3" /> View</>}
                          </button>
                          {st === 'draft' && canSubmit && (
                            <button onClick={() => handleSubmit(e.id)} disabled={isActing}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
                              {isActing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Submit
                            </button>
                          )}
                          {st === 'submitted' && canVerify && (
                            <button onClick={() => handleVerify(e.id)} disabled={isActing}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50">
                              <ShieldCheck className="w-3 h-3" /> Verify
                            </button>
                          )}
                          {st === 'dm_verified' && canApprove && (
                            <button onClick={() => handleApprove(e.id)} disabled={isActing}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                              <CheckCircle className="w-3 h-3" /> Approve
                            </button>
                          )}
                          {['submitted', 'dm_verified'].includes(st) && (canVerify || canApprove) && (
                            <button onClick={() => { setRejectModal(e.id); setRejectReason(''); }}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg">
                              <XCircle className="w-3 h-3" /> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rejectModal !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reject Unloading Entry</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..." rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim() || actionLoading === rejectModal}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50">
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnloadingEntriesList;
